import { GooglePlacesAutocomplete } from '@/components/selects/GooglePlacesAutocomplete';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import Grid from '@mui/material/Grid';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import OptionSelectField from '../OptionSelectField';
import FormikTextField from '../FormikTextField';
import { InsightSelectFormikWrapper } from '../InsightSelectFormikWrapper';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { getGridLayout } from '@/utils/fieldLayout';
import { FormLabel } from '@mui/material';
import { isProspectFieldVisible } from '@/features/config/helper';

const GROUP = 'address';

const AddressFields = ({ disabled }) => {
	const { setFieldValue } = useFormikContext();
	const { view, fieldDensity, isProspect } = useClientSchemaContext();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, isLoading } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const isAustralian = countryCode === 'AU';

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice();
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data]);

	const states = fields.find((f) => f.name === 'StateId')?.field?.options ?? [];
	const countries = fields.find((f) => f.name === 'CountryId')?.field?.options ?? [];

	const labelStyle = 'subtle';

	const sizeSchema = useMemo(() => getGridLayout(fields, fieldDensity), [fields, fieldDensity]);
	const getField = useCallback(
		(field) => {
			// console.log('Field name', field.name);
			const size = sizeSchema[field.name];
			switch (field.name) {
				case 'StateId':
				case 'CountryId':
					return (
						<Grid {...{ size }} key={field.name}>
							{['AU', 'NZ'].includes(countryCode) ? (
								<OptionSelectField {...{ field, disabled, labelStyle }} />
							) : (
								<InsightSelectFormikWrapper {...{ field, disabled, labelStyle }} />
							)}
						</Grid>
					);
				case 'AddressTypeId':
				case 'AreaId':
					return (
						<Grid {...{ size }} key={field.name}>
							<InsightSelectFormikWrapper {...{ field, disabled, labelStyle }} />
						</Grid>
					);
				default:
					return (
						<Grid {...{ size }} key={field.name}>
							<FormikTextField {...{ field, disabled, labelStyle }} />
						</Grid>
					);
			}
		},
		[fields, disabled, sizeSchema, countryCode, labelStyle],
	);

	if (fields.length === 0 || isLoading) return <></>;

	return (
		<Grid container rowSpacing={1} columnSpacing={2}>
			<Grid size={12}>
				<FormLabel>Address</FormLabel>
				<GooglePlacesAutocomplete
					onSelectAddress={(params) => {
						console.log('Selected address', params);

						setFieldValue('AddressLine1', params.addressLine1 ?? '');
						setFieldValue('AddressLine2', params.addressLine2 ?? '');
						setFieldValue('AddressLine3', params.addressLine3 ?? '');
						setFieldValue('Suburb', (isAustralian ? params.city : params.suburb) ?? '');
						setFieldValue('City', params.city ?? '');
						setFieldValue('Postcode', params.postcode ?? '');
						const newState = states.find(
							(v) => v.label.toUpperCase() === (params.state ?? '').toUpperCase(),
						);
						setFieldValue('StateId', newState?.value ?? '');
						const newCountry = countries.find(
							(v) => v.label.toUpperCase() === (params.country ?? '').toUpperCase(),
						);
						setFieldValue('CountryId', newCountry?.value ?? '');
					}}
					disabled={disabled}
				/>
			</Grid>

			{fields.map((field) => getField(field))}
		</Grid>
	);
};
export default AddressFields;
